import React, { useCallback } from 'react';
import { Box, Button, Flex, Heading, Text } from '@televet/kibble-ui';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from '../../../../routes';
import { useEmailTemplates } from 'shared/components/EmailEditor';
import { EmailTemplateType } from 'shared/types/graphql';
import format from 'date-fns/format';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';

export const ReminderEmailDeliveryTemplateEdit = (): JSX.Element | null => {
  // used for Preview Image Placeholder - implementation moved to phase 2
  const history = useHistory();

  const { draft, published } = useEmailTemplates(EmailTemplateType.ServiceReminder);

  const getByline = (firstName?: string, lastName?: string): string => {
    return firstName && lastName ? `by ${firstName} ${lastName}` : '';
  };

  const { primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();

  const { currentClinicId, currentClinic } = useClinicUser();
  const { trackEvent } = useTracking();

  const onEditTemplateClick = useCallback(() => {
    trackEvent(ServiceReminderEvents.EditEmailTemplateClick, {
      clinicId: currentClinicId,
      integrationType: primaryIntegration?.type,
      clinicName: currentClinic?.name,
      pims: primaryIntegrationName || '',
    });
    history.push(RouteBasePaths.EmailTemplates);
  }, [currentClinic?.name, currentClinicId, history, primaryIntegration?.type, primaryIntegrationName, trackEvent]);

  return (
    <Box>
      <Heading size="xs" mb={1}>
        Email Delivery Template
      </Heading>
      <Text as="p" maxWidth="500px" variant="subtle" mb={3} size="sm">
        This is the email that clients will receive when a reminder sends with the email delivery option enabled.{' '}
      </Text>
      <Flex align="center">
        <Box>
          {published && (
            <Text as="p" size="sm" mb={1}>
              <Text fontWeight="bold">Published: </Text>
              {format(new Date(published.publishedAt), 'MM/dd/yy h:mm a ')}
              {getByline(published.lastEditedBy?.firstName, published.lastEditedBy?.lastName)}
            </Text>
          )}
          {draft && (
            <Text as="p" size="sm" mb={1}>
              <Text fontWeight="bold">Draft saved: </Text>
              {format(new Date(draft.updatedAt), 'MM/dd/yy h:mm a ')}
              {getByline(draft.lastEditedBy?.firstName, draft.lastEditedBy?.lastName)}
            </Text>
          )}
          <Button variant="secondary" iconName="pen" size="sm" mt={2} onClick={onEditTemplateClick}>
            Edit Email Template
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};
